import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import {
    Box,
    CircularProgress,
    Grid,
    Paper,
    Tab,
    Tabs,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    Link,
    Chip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RootState } from '@features/redux/store';
import { getRecentStationMoveActivity, getStationMoveRequests } from '@api/system';
import { AppRoutes } from '@consts/routes';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useNameMapQuery } from '@features/redux/apis/org';

type PendingMove = {
    MoveRequestID: number;
    StationID: number;
    TargetLocationID: number;
    LocationName: string;
    TargetLocationName: string;
    EffectiveDate: string;
    Status: 'Pending';
};

type RecentMove = Omit<PendingMove, 'Status'> & {
    Status: 'Executed' | 'Cancelled';
    LastStatusUpdate: string;
};

dayjs.extend(utc);

export const StationMoveActivity = () => {
    const { IdToken } = useSelector((state: RootState) => state.auth);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const carouselRef = useRef<HTMLDivElement>(null);

    const { data: nameMap } = useNameMapQuery({});

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState<PendingMove[]>([]);
    const [recent, setRecent] = useState<RecentMove[]>([]);

    useEffect(() => {
        if (!IdToken) return;
        setLoading(true);
        Promise.all([getStationMoveRequests(IdToken), getRecentStationMoveActivity(IdToken)])
            .then(([pendingResp, recentResp]) => {
                setPending(pendingResp || []);
                setRecent(recentResp || []);
            })
            .finally(() => setLoading(false));
    }, [IdToken]);

    const executedThisWeek = useMemo(() => {
        const weekStart = dayjs().startOf('week');
        return recent.filter((r) => r.Status === 'Executed' && dayjs(r.LastStatusUpdate).isAfter(weekStart));
    }, [recent]);

    const sortedRecent = useMemo(
        () =>
            [...recent].sort(
                (a, b) => dayjs.utc(b.LastStatusUpdate).valueOf() - dayjs.utc(a.LastStatusUpdate).valueOf()
            ),
        [recent]
    );

    return (
        <BasePage>
            <BreadcrumbSubBar />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={1} flexDirection='row' gap={1} p={1}>
                    {/* Pending Requests */}
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Paper
                            sx={{
                                p: 2,
                                border: '1px solid #e0e0e0',
                                maxHeight: '500px',
                                overflowY: 'auto',
                            }}
                            elevation={1}>
                            <Typography variant='h6' fontWeight={700} mb={2}>
                                Pending Station Moves
                            </Typography>
                            {loading ? (
                                <Box display='flex' justifyContent='center' my={3}>
                                    <CircularProgress size='2rem' />
                                </Box>
                            ) : pending.length ? (
                                pending.map((item) => (
                                    <Paper
                                        key={`pending-${item.MoveRequestID}`}
                                        sx={{ p: 2, mb: 1, border: '1px solid #e0e0e0', borderRadius: 2 }}
                                        elevation={0}>
                                        <Box display='flex' justifyContent='space-between' alignItems='center' pb={1}>
                                            <Box display='flex' alignItems='center' gap={1}>
                                                <Typography variant='subtitle2' fontWeight={600}>
                                                    {`Station #${item.StationID}`}
                                                </Typography>
                                                <Chip
                                                    label={`${item.LocationName} → ${item.TargetLocationName}`}
                                                    size='small'
                                                    sx={{
                                                        backgroundColor: 'rgba(25, 118, 210, 0.18)',
                                                        color: 'rgba(25, 118, 210, 1)',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        height: 20,
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Typography variant='body2' color='text.primary'>
                                            {`Effective: ${dayjs(item.EffectiveDate).format('MMM DD, YYYY')}`}
                                        </Typography>
                                        {/* Creator email not included in PendingMove type here; omit for now */}

                                        <Box
                                            display={'flex'}
                                            gap={1}
                                            flexDirection={'row'}
                                            justifyContent={'space-between'}
                                            flex={1}>
                                            <Box mt={0.5}>
                                                <Link
                                                    component={RouterLink}
                                                    to={`${AppRoutes.Organizations}?type=system&id=${item.StationID}`}
                                                    underline='hover'
                                                    fontSize='0.8rem'>
                                                    View System Details
                                                </Link>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))
                            ) : (
                                <Typography>No pending requests.</Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* Recent Activity */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2, border: '1px solid #e0e0e0', overflowY: 'auto' }} elevation={1}>
                            <Typography variant='h6' fontWeight={700} mb={2}>
                                Recent Activity
                            </Typography>
                            {loading ? (
                                <Box display='flex' justifyContent='center' my={3}>
                                    <CircularProgress size='2rem' />
                                </Box>
                            ) : sortedRecent.length ? (
                                isMobile ? (
                                    sortedRecent.map((r) => (
                                        <Paper
                                            key={`recent-${r.MoveRequestID}`}
                                            sx={{ p: 2, mb: 1, border: '1px solid #e0e0e0', borderRadius: 2 }}
                                            elevation={0}>
                                            <Box display='flex' alignItems='center' gap={1}>
                                                <Typography variant='subtitle2' fontWeight={600}>
                                                    {`Station #${r.StationID}`}
                                                </Typography>
                                                <Chip
                                                    label={`→ ${r.TargetLocationName}`}
                                                    size='small'
                                                    sx={{
                                                        backgroundColor: 'rgba(25, 118, 210, 0.18)',
                                                        color: 'rgba(25, 118, 210, 1)',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        height: 20,
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant='body2' color='text.primary' sx={{ mt: 0.5 }}>
                                                {`Effective: ${dayjs(r.EffectiveDate).format('MMM DD, YYYY')} — ${
                                                    r.Status
                                                } at ${dayjs(r.LastStatusUpdate).format('YYYY-MM-DD HH:mm')}`}
                                            </Typography>
                                            <Box mt={0.5}>
                                                <Link
                                                    component={RouterLink}
                                                    to={`${AppRoutes.Organizations}?type=system&id=${r.StationID}`}
                                                    underline='hover'
                                                    fontSize='0.8rem'>
                                                    View System Details
                                                </Link>
                                            </Box>
                                        </Paper>
                                    ))
                                ) : (
                                    <Box position='relative'>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                carouselRef.current?.scrollBy({
                                                    left: -300,
                                                    behavior: 'smooth',
                                                })
                                            }
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 1,
                                            }}>
                                            <ChevronLeft />
                                        </IconButton>
                                        <Box
                                            ref={carouselRef}
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                overflowX: 'auto',
                                                scrollBehavior: 'smooth',
                                                p: 1,
                                                mx: 4,
                                            }}>
                                            {sortedRecent.map((r) => (
                                                <Paper
                                                    key={`recent-${r.MoveRequestID}`}
                                                    sx={{
                                                        p: 2,
                                                        minWidth: 320,
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 2,
                                                    }}
                                                    elevation={0}>
                                                    <Box display='flex' alignItems='center' gap={1}>
                                                        <Typography variant='subtitle2' fontWeight={600}>
                                                            {`Station #${r.StationID}`}
                                                        </Typography>
                                                        <Chip
                                                            label={`→ ${r.TargetLocationName}`}
                                                            size='small'
                                                            sx={{
                                                                backgroundColor: 'rgba(25, 118, 210, 0.18)',
                                                                color: 'rgba(25, 118, 210, 1)',
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                                height: 20,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant='body2' color='text.primary' sx={{ mt: 0.5 }}>
                                                        {`Effective: ${dayjs(r.EffectiveDate).format(
                                                            'MMM DD, YYYY'
                                                        )} — ${r.Status} at ${dayjs(r.LastStatusUpdate).format(
                                                            'YYYY-MM-DD HH:mm'
                                                        )}`}
                                                    </Typography>
                                                    <Box mt={0.5}>
                                                        <Link
                                                            component={RouterLink}
                                                            to={`${AppRoutes.Organizations}?type=system&id=${r.StationID}`}
                                                            underline='hover'
                                                            fontSize='0.8rem'>
                                                            View System Details
                                                        </Link>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Box>
                                        <IconButton
                                            size='small'
                                            onClick={() =>
                                                carouselRef.current?.scrollBy({ left: 300, behavior: 'smooth' })
                                            }
                                            sx={{
                                                position: 'absolute',
                                                right: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                            }}>
                                            <ChevronRight />
                                        </IconButton>
                                    </Box>
                                )
                            ) : (
                                <Typography>No recent activity.</Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </div>
        </BasePage>
    );
};
